// ===============================================
// File: src/contexts/POContext.jsx
// Description: Central store for purchase orders.
// Unified persistence (Folder OR Drive) via StorageContext.
// - If no workspace linked → in-memory empty; writes are no-ops
// - Rehydrates on boot and after restore (vault/files)
// - Public API unchanged
// ===============================================

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { useStorage } from "./StorageContext";

const FILE_PATH = "purchase-orders/orders.json";

export const POContext = createContext({
  orders: [],
  addOrder: () => {},
  updateOrder: () => {},
  removeOrder: () => {},
  clearAllOrders: () => {},
});

export function POProvider({ children }) {
  const {
    hasFolder,
    isDriveLinked,
    isDriveAuthed,
    loadJSON,
    saveJSON,
  } = useStorage();

  const hasWorkspace = !!hasFolder || (!!isDriveLinked && !!isDriveAuthed);

  const [orders, setOrders] = useState([]);
  const loadedRef = useRef(false);
  const saveDebounce = useRef(0);

  // keep latest funcs in refs
  const loadRef = useRef(loadJSON);
  const saveRef = useRef(saveJSON);
  useEffect(() => { loadRef.current = loadJSON; }, [loadJSON]);
  useEffect(() => { saveRef.current = saveJSON; }, [saveJSON]);

  // ---- load from workspace (or empty if none) ----
  useEffect(() => {
    let alive = true;
    loadedRef.current = false;

    (async () => {
      if (!hasWorkspace) {
        if (alive) setOrders([]);
        loadedRef.current = true;
        return;
      }
      try {
        const disk = await loadRef.current(FILE_PATH, []);
        if (alive) setOrders(Array.isArray(disk) ? disk : []);
      } catch {
        if (alive) setOrders([]);
      } finally {
        loadedRef.current = true;
      }
    })();

    return () => { alive = false; };
  }, [hasWorkspace]);

  // ---- rehydrate after restore (vault or files) ----
  useEffect(() => {
    const onRestored = async () => {
      if (!hasWorkspace) return;
      try {
        const disk = await loadRef.current(FILE_PATH, []);
        setOrders(Array.isArray(disk) ? disk : []);
      } catch {}
    };
    
    const onDisconnect = () => {
      // CRITICAL: Clear all data when workspace disconnected
      setOrders([]);
      loadedRef.current = false;
    };
    
    window.addEventListener("mudo:vault-restored", onRestored);
    window.addEventListener("mudo:files-restored", onRestored);
    window.addEventListener("mudo:workspace-disconnected", onDisconnect);
    return () => {
      window.removeEventListener("mudo:vault-restored", onRestored);
      window.removeEventListener("mudo:files-restored", onRestored);
      window.removeEventListener("mudo:workspace-disconnected", onDisconnect);
    };
  }, [hasWorkspace]);

  // ---- persist (debounced) ----
  useEffect(() => {
    if (!loadedRef.current || !hasWorkspace) return;
    clearTimeout(saveDebounce.current);
    saveDebounce.current = setTimeout(async () => {
      try { await saveRef.current(FILE_PATH, orders); } catch {}
    }, 300);
    return () => clearTimeout(saveDebounce.current);
  }, [orders, hasWorkspace]);

  // ---- CRUD ----
  const addOrder = (order) => {
    const withId = {
      ...order,
      id: order?.id || (crypto?.randomUUID?.() || `PO-${Date.now()}`),
      createdAt: order?.createdAt || Date.now(),
      updatedAt: Date.now(),
    };
    setOrders((prev) => [withId, ...(Array.isArray(prev) ? prev : [])]);
  };

  const updateOrder = (updated) => {
    if (!updated?.id) return;
    setOrders((prev) =>
      (Array.isArray(prev) ? prev : []).map((o) =>
        String(o.id) === String(updated.id)
          ? { ...o, ...updated, updatedAt: Date.now() }
          : o
      )
    );
  };

  const removeOrder = (id) => {
    setOrders((prev) =>
      (Array.isArray(prev) ? prev : []).filter((o) => String(o.id) !== String(id))
    );
  };

  const clearAllOrders = () => setOrders([]);

  const value = useMemo(
    () => ({
      orders,
      addOrder,
      updateOrder,
      removeOrder,
      clearAllOrders,
    }),
    [orders]
  );

  return <POContext.Provider value={value}>{children}</POContext.Provider>;
}

/* ---------- Hooks ---------- */
export const usePOs = () => useContext(POContext);
export const usePO = () => useContext(POContext);
